import { useCallback, useEffect, useState } from 'react';
import { registerService } from '@/services/api';
import { Stack, CircularProgress, Alert } from '@mui/material';
import { useRegistrationStore } from '@/store/pages/registrationStore/registrationStore';
import { usePhrase } from '@/hooks/general';
import { IDENTITY_PHRASES } from '@/consts/phrases/registrationPhrases';
import { getFetchErrors } from '@/utils/getFetchErrorMessage';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { YotiIFrame } from '@/components/blocks/identity/YotiIFrame/YotiIFrame';
import { FetchErrorsType, IAllRegistrationData } from '@/types/types';
import * as Sentry from '@sentry/nextjs';
import { useYotiWatchHandler } from '@/hooks/identity';

interface IdentityRegistrationYotiFrameProps {
    registrationId: string;
    yotiId?: string | null;
    yotiToken?: string | null;
    isMobile?: boolean;
    setErrorMessage?: (err: FetchErrorsType) => void;
    revalidateRegistrationData?: () => Promise<void>;
}

export const IdentityRegistrationYotiFrame = ({
    registrationId,
    yotiId,
    yotiToken,
    isMobile = false,
    setErrorMessage,
    revalidateRegistrationData,
}: IdentityRegistrationYotiFrameProps) => {
    const { t } = usePhrase();
    const [sessionId, setSessionId] = useState<string | null | undefined>(yotiId);
    const [sessionToken, setSessionToken] = useState<string | null | undefined>(yotiToken);
    const [mobileErrorMessage, setMobileErrorMessage] = useState<FetchErrorsType>(undefined);
    const [successMessage, setSuccessMessage] = useState<boolean>(false);

    const { goToNextStep, reset } = useRegistrationStore((state) => ({
        goToNextStep: state.goToNextStep,
        reset: state.reset,
    }));

    const handleErrorMessage = useCallback(
        (err: unknown) => {
            if (isMobile) {
                setMobileErrorMessage(getFetchErrors(err));
            } else if (setErrorMessage) {
                setErrorMessage(getFetchErrors(err));
            }
        },
        [isMobile, setErrorMessage],
    );

    const handleRegistrationIdExist = useCallback(() => {
        if (!registrationId) {
            Sentry.captureException(
                new Error('Attempted to submit form but no registration id existed'),
            );
            reset();
            return;
        }
    }, [registrationId, reset]);

    useEffect(() => {
        if (!sessionId || !sessionToken) {
            void (async () => {
                let registrationData: IAllRegistrationData | undefined = undefined;
                try {
                    handleRegistrationIdExist();
                    registrationData = await registerService.getRegistrationData(registrationId);
                    const { yotiSessionID, yotiSessionToken } = registrationData;
                    if (!yotiSessionID || !yotiSessionToken) {
                        throw new Error('Recieved Yoti data is incorrect');
                    } else {
                        setSessionId(yotiSessionID);
                        setSessionToken(yotiSessionToken);
                    }
                } catch (err) {
                    Sentry.captureException(err, {
                        extra: { registrationData },
                    });
                    handleErrorMessage(err);
                }
            })();
        }
    }, [
        sessionId,
        sessionToken,
        registrationId,
        isMobile,
        setErrorMessage,
        handleErrorMessage,
        handleRegistrationIdExist,
    ]);

    const onYotiSuccess = useCallback(() => {
        if (isMobile) {
            setSuccessMessage(true);
        }
        void (async () => {
            try {
                handleRegistrationIdExist();
                await registerService.finishIdentity(registrationId);
                if (!isMobile) {
                    goToNextStep();
                    await revalidateRegistrationData?.();
                }
            } catch (err) {
                handleErrorMessage(err);
            }
        })();
    }, [
        goToNextStep,
        handleErrorMessage,
        handleRegistrationIdExist,
        isMobile,
        registrationId,
        revalidateRegistrationData,
    ]);

    useYotiWatchHandler(onYotiSuccess);

    return (
        <>
            {isMobile && mobileErrorMessage && (
                <ErrorAlert
                    id="error_message_box"
                    data-testid="mobileError"
                    errorMessage={mobileErrorMessage}
                />
            )}
            {successMessage && !mobileErrorMessage && (
                <Alert severity="success" id="success_message_box">
                    {t(IDENTITY_PHRASES.MOBILE_COMPLETE_SUCCESS_TEXT)}
                </Alert>
            )}
            {sessionId && sessionToken && !successMessage ? (
                <YotiIFrame sessionId={sessionId} sessionToken={sessionToken} />
            ) : (
                <>
                    {!mobileErrorMessage && !successMessage && (
                        <Stack
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '450px',
                            }}
                        >
                            <CircularProgress data-testid="yotiPreloader" />
                        </Stack>
                    )}
                </>
            )}
        </>
    );
};
